/**
 * 双色球（福彩）前端业务配置与注数计算
 * 与超级大乐透（dlt.js）并列；勿混用 front/back 字段
 * 对齐后端：gameId=17 / gameCode=SSQ / ssqMath.js / ssqSettle.js
 */

export const SSQ_GAME_ID = 17
export const SSQ_GAME_NAME = '双色球'
export const SSQ_GAME_CODE = 'SSQ'

export const SSQ_RED_MIN = 1
export const SSQ_RED_MAX = 33
export const SSQ_BLUE_MIN = 1
export const SSQ_BLUE_MAX = 16
export const SSQ_RED_PICK = 6
export const SSQ_BLUE_PICK = 1
export const SSQ_MAX_RED_MULTI = 20

/** 单注 2 U；无追加 */
export const SSQ_UNIT_PRICE = 2

export const SSQ_RED_NUMS = Array.from({ length: SSQ_RED_MAX }, (_, i) => i + 1)
export const SSQ_BLUE_NUMS = Array.from({ length: SSQ_BLUE_MAX }, (_, i) => i + 1)

export function pad2(n) {
  return String(n).padStart(2, '0')
}

/** 组合数 C(n,k) — 与 backend/ssqMath.js 同口径 */
export function comb(n, k) {
  const nn = Number(n)
  const kk = Number(k)
  if (!Number.isFinite(nn) || !Number.isFinite(kk) || kk < 0 || kk > nn) return 0
  if (kk === 0 || kk === nn) return 1
  const m = Math.min(kk, nn - kk)
  let r = 1
  for (let i = 1; i <= m; i++) r = (r * (nn - m + i)) / i
  return Math.round(r)
}

export const SSQ_PLAY_MODE_TABS = [
  { key: 'tradition', label: '官方' }
]

/** 更多玩法抽屉分组（仅传统盘） */
export const SSQ_MENU_GROUPS = [
  {
    key: 'basic',
    label: '基本投注',
    items: [
      { key: 'ssq-single', label: '单式' }
    ]
  },
  {
    key: 'multi',
    label: '复式投注',
    items: [
      { key: 'ssq-red-multi', label: '红球复式' },
      { key: 'ssq-blue-multi', label: '蓝球复式' },
      { key: 'ssq-both-multi', label: '全复式' }
    ]
  },
  {
    key: 'dantuo',
    label: '胆拖投注',
    items: [
      { key: 'ssq-red-dantuo', label: '红胆拖' },
      { key: 'ssq-rb-dantuo', label: '红蓝胆拖' }
    ]
  }
]

export const SSQ_PLAYS = {
  'ssq-single': {
    key: 'ssq-single',
    playName: '单式',
    mode: 'single',
    group: 'basic',
    tip: '红球选 6 个、蓝球选 1 个号码'
  },
  'ssq-red-multi': {
    key: 'ssq-red-multi',
    playName: '红球复式',
    mode: 'multi',
    group: 'multi',
    tip: `红球选 7–${SSQ_MAX_RED_MULTI} 个、蓝球选 1 个`
  },
  'ssq-blue-multi': {
    key: 'ssq-blue-multi',
    playName: '蓝球复式',
    mode: 'multi',
    group: 'multi',
    tip: '红球选 6 个、蓝球至少选 2 个'
  },
  'ssq-both-multi': {
    key: 'ssq-both-multi',
    playName: '全复式',
    mode: 'multi',
    group: 'multi',
    tip: `红球至少 7 个、蓝球至少 2 个（红球上限 ${SSQ_MAX_RED_MULTI}）`
  },
  'ssq-red-dantuo': {
    key: 'ssq-red-dantuo',
    playName: '红胆拖',
    mode: 'dantuo',
    group: 'dantuo',
    tip: '红胆 1–5、拖码补足（胆+拖≥7）；蓝球固定 1 个'
  },
  'ssq-rb-dantuo': {
    key: 'ssq-rb-dantuo',
    playName: '红蓝胆拖',
    mode: 'dantuo',
    group: 'dantuo',
    tip: '红球胆拖 + 蓝球至少 2 个'
  }
}

export function getSsqPlay(key) {
  return SSQ_PLAYS[key] || null
}

export function findSsqPlayLabel(key) {
  return SSQ_PLAYS[key]?.playName || ''
}

export function findSsqGroupByPlayKey(key) {
  return SSQ_MENU_GROUPS.find((g) => g.items.some((it) => it.key === key)) || null
}

export function isSsqPlayKey(key) {
  return !!SSQ_PLAYS[key]
}

/**
 * 计算注数；不合法返回 { error, count: 0 }
 * @param {string} playKey
 * @param {{
 *   red?: number[], blue?: number[],
 *   redDan?: number[], redTuo?: number[]
 * }} sel
 */
export function computeSsqStakeCount(playKey, sel = {}) {
  const play = getSsqPlay(playKey)
  if (!play) return { error: '未知玩法', count: 0 }

  const red = uniqNums(sel.red, SSQ_RED_MIN, SSQ_RED_MAX)
  if (red.error && play.mode !== 'dantuo') return { error: red.error, count: 0 }
  const blue = uniqNums(sel.blue, SSQ_BLUE_MIN, SSQ_BLUE_MAX)
  if (blue.error && play.mode !== 'dantuo') return { error: blue.error, count: 0 }

  if (play.mode === 'single') {
    if (red.nums.length !== SSQ_RED_PICK || blue.nums.length !== SSQ_BLUE_PICK) {
      return { error: '单式须红球恰 6 个、蓝球恰 1 个', count: 0 }
    }
    return { count: 1 }
  }

  if (play.mode === 'multi') {
    return countMulti(playKey, red.nums, blue.nums)
  }

  return countDantuo(playKey, sel)
}

function countMulti(playKey, red, blue) {
  const rn = red.length
  const bn = blue.length
  if (playKey === 'ssq-red-multi') {
    if (rn < 7 || rn > SSQ_MAX_RED_MULTI || bn !== SSQ_BLUE_PICK) {
      return { error: `红球复式须红球 7–${SSQ_MAX_RED_MULTI}、蓝球=1`, count: 0 }
    }
    return { count: comb(rn, SSQ_RED_PICK) }
  }
  if (playKey === 'ssq-blue-multi') {
    if (rn !== SSQ_RED_PICK || bn < 2) {
      return { error: '蓝球复式须红球=6、蓝球≥2', count: 0 }
    }
    return { count: bn }
  }
  if (playKey === 'ssq-both-multi') {
    if (rn < 7 || rn > SSQ_MAX_RED_MULTI || bn < 2) {
      return { error: `全复式须红球 7–${SSQ_MAX_RED_MULTI}、蓝球≥2`, count: 0 }
    }
    return { count: comb(rn, SSQ_RED_PICK) * bn }
  }
  return { error: '复式玩法无效', count: 0 }
}

function countDantuo(playKey, sel) {
  const rd = uniqNums(sel.redDan, SSQ_RED_MIN, SSQ_RED_MAX)
  if (rd.error) return { error: rd.error, count: 0 }
  const rt = uniqNums(sel.redTuo, SSQ_RED_MIN, SSQ_RED_MAX)
  if (rt.error) return { error: rt.error, count: 0 }
  const blue = uniqNums(sel.blue, SSQ_BLUE_MIN, SSQ_BLUE_MAX)
  if (blue.error) return { error: blue.error, count: 0 }

  if (overlap(rd.nums, rt.nums)) return { error: '红球胆码与拖码不可重叠', count: 0 }
  if (rd.nums.length < 1 || rd.nums.length > 5) return { error: '红球胆码须 1–5 个', count: 0 }
  if (rd.nums.length + rt.nums.length < 7) return { error: '红球胆+拖须≥7', count: 0 }
  const needRed = SSQ_RED_PICK - rd.nums.length
  if (rt.nums.length < needRed) return { error: '红球拖码不足', count: 0 }

  if (playKey === 'ssq-rb-dantuo') {
    if (blue.nums.length < 2) return { error: '红蓝胆拖须蓝球≥2', count: 0 }
    return { count: comb(rt.nums.length, needRed) * blue.nums.length }
  }

  if (blue.nums.length !== SSQ_BLUE_PICK) {
    return { error: '红胆拖须蓝球恰 1 个', count: 0 }
  }
  return { count: comb(rt.nums.length, needRed) }
}

function uniqNums(arr, min, max) {
  const set = new Set()
  for (const x of arr || []) {
    const n = Number(x)
    if (!Number.isInteger(n) || n < min || n > max) {
      return { error: `号码须为 ${pad2(min)}–${pad2(max)} 的整数`, nums: [] }
    }
    if (set.has(n)) return { error: '号码不可重复', nums: [] }
    set.add(n)
  }
  return { nums: [...set].sort((a, b) => a - b) }
}

function overlap(a, b) {
  const s = new Set(a)
  return b.some((n) => s.has(n))
}

/** 金额 = 注数 × 2 × 倍数（无追加） */
export function computeSsqAmount(count, multiple = 1) {
  const stakes = Math.max(0, Number(count) || 0)
  const mult = Math.min(99, Math.max(1, Math.floor(Number(multiple) || 1)))
  return +(stakes * SSQ_UNIT_PRICE * mult).toFixed(2)
}

export function formatSsqSelectionContent(playKey, sel = {}) {
  const name = findSsqPlayLabel(playKey) || playKey
  const play = getSsqPlay(playKey)
  if (play?.mode === 'dantuo') {
    const parts = []
    if (sel.redDan?.length) parts.push(`红胆:${sel.redDan.map(pad2).join(',')}`)
    if (sel.redTuo?.length) parts.push(`红拖:${sel.redTuo.map(pad2).join(',')}`)
    const blue = (sel.blue || []).map(pad2).join(' ')
    return `${name} ${parts.join(' ')}${blue ? ` + ${blue}` : ''}`.trim()
  }
  const red = (sel.red || []).map(pad2).join(' ')
  const blue = (sel.blue || []).map(pad2).join(' ')
  if (red && blue) return `${name} ${red} + ${blue}`
  if (red) return `${name} ${red}`
  if (blue) return `${name} ${blue}`
  return name
}

/** 机选：从区间随机不重复 k 个 */
export function randomPick(min, max, k) {
  const pool = Array.from({ length: max - min + 1 }, (_, i) => min + i)
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]]
  }
  return pool.slice(0, k).sort((a, b) => a - b)
}

/**
 * 按玩法机选一注方案
 * @param {string} playKey
 */
export function randomSsqTicket(playKey = 'ssq-single') {
  const key = String(playKey || 'ssq-single')

  if (key === 'ssq-red-multi' || key === 'red-multi') {
    return {
      red: randomPick(SSQ_RED_MIN, SSQ_RED_MAX, 7),
      blue: randomPick(SSQ_BLUE_MIN, SSQ_BLUE_MAX, 1)
    }
  }
  if (key === 'ssq-blue-multi' || key === 'blue-multi') {
    return {
      red: randomPick(SSQ_RED_MIN, SSQ_RED_MAX, 6),
      blue: randomPick(SSQ_BLUE_MIN, SSQ_BLUE_MAX, 3)
    }
  }
  if (key === 'ssq-both-multi' || key === 'both-multi') {
    return {
      red: randomPick(SSQ_RED_MIN, SSQ_RED_MAX, 7),
      blue: randomPick(SSQ_BLUE_MIN, SSQ_BLUE_MAX, 2)
    }
  }
  if (key === 'ssq-red-dantuo') {
    const pool = randomPick(SSQ_RED_MIN, SSQ_RED_MAX, 7)
    return {
      redDan: pool.slice(0, 2),
      redTuo: pool.slice(2),
      blue: randomPick(SSQ_BLUE_MIN, SSQ_BLUE_MAX, 1)
    }
  }
  if (key === 'ssq-rb-dantuo') {
    const pool = randomPick(SSQ_RED_MIN, SSQ_RED_MAX, 7)
    return {
      redDan: pool.slice(0, 2),
      redTuo: pool.slice(2),
      blue: randomPick(SSQ_BLUE_MIN, SSQ_BLUE_MAX, 3)
    }
  }
  return {
    red: randomPick(SSQ_RED_MIN, SSQ_RED_MAX, 6),
    blue: randomPick(SSQ_BLUE_MIN, SSQ_BLUE_MAX, 1)
  }
}

export const SSQ_PRIZE_CAPS = {
  periodMax: '400000.0000',
  betMax: '200000.0000',
  stakeMax: '50000.0000'
}

/** 追号上限（与后端 listSsqChaseNumeros / placeOrder 一致） */
export const SSQ_CHASE_MAX = 20

/**
 * 六奖级演示固定额（U / 注 / 倍）
 * 须与 backend/src/ssqSettle.js 的 SSQ_PRIZE_TABLE 保持一致
 */
export const SSQ_PRIZE_AMOUNTS = {
  1: 10000000,
  2: 500000,
  3: 3000,
  4: 200,
  5: 10,
  6: 5
}

export const SSQ_PRIZE_GRADES = [
  { grade: '一等奖', hit: '6红 + 蓝', basic: '浮动（演示 10,000,000）', amount: SSQ_PRIZE_AMOUNTS[1] },
  { grade: '二等奖', hit: '6红 + 0蓝', basic: '浮动（演示 500,000）', amount: SSQ_PRIZE_AMOUNTS[2] },
  { grade: '三等奖', hit: '5红 + 蓝', basic: '3,000', amount: SSQ_PRIZE_AMOUNTS[3] },
  { grade: '四等奖', hit: '5红+0蓝 或 4红+蓝', basic: '200', amount: SSQ_PRIZE_AMOUNTS[4] },
  { grade: '五等奖', hit: '4红+0蓝 或 3红+蓝', basic: '10', amount: SSQ_PRIZE_AMOUNTS[5] },
  { grade: '六等奖', hit: '2/1/0红 + 蓝', basic: '5', amount: SSQ_PRIZE_AMOUNTS[6] }
]

export const SSQ_PLAY_HELP = {
  'ssq-single': {
    title: '单式',
    desc: '从红球 01–33 中选 6 个、蓝球 01–16 中选 1 个组成 1 注，单注 2 U。双色球无追加投注。',
    example: '例如红球 01 05 12 18 22 27，蓝球 09，共 1 注。'
  },
  'ssq-red-multi': {
    title: '红球复式',
    desc: `红球选择 7–${SSQ_MAX_RED_MULTI} 个号码，蓝球选择 1 个。注数 = C(红球个数, 6)。`,
    example: '红球选 7 个、蓝球 1 个 → C(7,6)=7 注。'
  },
  'ssq-blue-multi': {
    title: '蓝球复式',
    desc: '红球选择 6 个号码，蓝球选择 2 个及以上。注数 = 蓝球个数。',
    example: '红球 6 个、蓝球 3 个 → 3 注。'
  },
  'ssq-both-multi': {
    title: '全复式',
    desc: `红球≥7（上限 ${SSQ_MAX_RED_MULTI}）、蓝球≥2。注数 = C(红,6) × 蓝球个数。`,
    example: '红 7 蓝 2 → 7 × 2 = 14 注。'
  },
  'ssq-red-dantuo': {
    title: '红胆拖',
    desc: '红球设胆码 1–5 个（须全部开出），其余为拖码；胆+拖≥7；蓝球固定选 1 个。注数 = C(拖码个数, 6−胆码个数)。胆码若未开出则整注不中。',
    example: '红胆 2 个、红拖 5 个、蓝球 1 个 → C(5,4)=5 注。'
  },
  'ssq-rb-dantuo': {
    title: '红蓝胆拖',
    desc: '红球胆拖规则同上，蓝球选择 2 个及以上。注数 = 红球胆拖注数 × 蓝球个数。胆码若未开出则整注不中。',
    example: '红胆拖算出 5 注、蓝球 3 个 → 共 15 注。'
  }
}

export function getSsqPlayHelp(playKey) {
  return SSQ_PLAY_HELP[playKey] || {
    title: findSsqPlayLabel(playKey) || '玩法说明',
    desc: '请按页面提示完成选号。',
    example: ''
  }
}

export const SSQ_LOTTERY_HELP = {
  schedules: [
    { time: '每周二、四、日 北京时间约 21:15', freq: '每周三期' }
  ],
  intro:
    '此彩种来源于中国福利彩票双色球。红球从 01–33 中开出 6 个号码，蓝球从 01–16 中开出 1 个号码。支持单式、复式、胆拖及倍投、追号（演示环境）。无追加投注。彩票开奖为随机事件，复式、胆拖、倍投仅增加购买组合数量，不会提高单注中奖概率。请理性购彩，量力而行；未成年人禁止购彩。',
  live: { label: '中国福彩网', url: 'https://www.cwl.gov.cn/' },
  official: { label: '中国福彩网', url: 'https://www.cwl.gov.cn/' },
  ref: 'https://www.cwl.gov.cn/'
}

/** 开奖号码拆分：前 6 红 + 末 1 蓝 */
export function splitSsqCodes(codes = []) {
  const list = (codes || []).map(Number).filter((n) => Number.isFinite(n))
  return {
    red: list.slice(0, 6),
    blue: list.slice(6, 7)
  }
}

/** 解析 winNo：`051218222733+09` 或纯数字数组 */
export function parseSsqWinNo(winNo) {
  if (Array.isArray(winNo)) return winNo.map(Number).filter((n) => Number.isFinite(n))
  const s = String(winNo || '').trim()
  if (!s) return []
  if (s.includes('+')) {
    const [a, b] = s.split('+')
    const red = String(a).match(/\d{2}/g) || []
    const blue = String(b).match(/\d{2}/g) || []
    return [...red, ...blue].map(Number)
  }
  const pairs = s.match(/\d{2}/g)
  if (pairs && pairs.length >= 7) return pairs.slice(0, 7).map(Number)
  return s.split(/[\s,]+/).map(Number).filter((n) => Number.isFinite(n))
}

export function formatSsqOpenTime(ts) {
  if (!ts) return '—'
  const d = new Date(Number(ts))
  if (Number.isNaN(d.getTime())) return '—'
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}
